/**
 * Free shipping bar behavior.
 *
 * The Liquid snippet re-renders inside the cart section on every cart update
 * (via `morphSection`), which handles width/message updates automatically
 * because we use a CSS width transition on `.ff-shipping-bar__fill`.
 *
 * This module only adds the *one-shot celebration*: when the qualified state
 * flips from false -> true, restart the confetti animation. It also suppresses
 * the burst on the initial page load so users returning to an already-qualified
 * cart don't get a spurious animation.
 */

const SELECTOR = '[data-free-shipping-bar]';
const CELEBRATING_CLASS = 'is-celebrating';

// Per-bar previous qualified state. Keyed by the element itself via WeakMap so
// removed drawers get garbage collected.
const previousQualified = new WeakMap();

/**
 * @param {Element} bar
 * @returns {boolean}
 */
function isQualified(bar) {
  return bar.getAttribute('data-qualified') === 'true';
}

/**
 * Restart the confetti animation on a single bar.
 * @param {HTMLElement} bar
 */
function celebrate(bar) {
  bar.classList.remove(CELEBRATING_CLASS);
  // Force reflow so re-adding the class restarts the CSS animations.
  void bar.offsetWidth;
  bar.classList.add(CELEBRATING_CLASS);
}

/**
 * For each bar currently in the DOM, compare its qualified state to the last
 * value we saw. If it just flipped false -> true, celebrate.
 */
function reconcileBars() {
  const bars = document.querySelectorAll(SELECTOR);
  bars.forEach((bar) => {
    if (!(bar instanceof HTMLElement)) return;
    const nowQualified = isQualified(bar);
    const prev = previousQualified.get(bar);

    if (prev === false && nowQualified === true) {
      celebrate(bar);
    }

    previousQualified.set(bar, nowQualified);
  });
}

/**
 * Seed the initial qualified state without celebrating. Called once on load
 * and whenever a fresh bar element appears (e.g. after cart goes empty->1 item
 * and the section morphs in a bar that wasn't there before).
 */
function seedInitialState() {
  const bars = document.querySelectorAll(SELECTOR);
  bars.forEach((bar) => {
    if (!(bar instanceof HTMLElement)) return;
    if (!previousQualified.has(bar)) {
      previousQualified.set(bar, isQualified(bar));
    }
  });
}

// Initial seed on script load. Cart section may render before or after this
// script executes; either way, the first observation is baseline (no confetti).
seedInitialState();

// The theme dispatches 'cart:update' synchronously *before* it calls
// morphSection to update the DOM (see component-cart-items.js: dispatchEvent
// runs first, then morphSection). If we reconcile in the event handler itself
// we'd read the stale pre-morph attributes. Defer to a microtask so morph has
// completed by the time we read data-qualified.
document.addEventListener('cart:update', () => {
  queueMicrotask(() => {
    // A fresh bar may have appeared (empty -> non-empty). Seed it first so we
    // don't celebrate on its very first observation.
    const bars = document.querySelectorAll(SELECTOR);
    bars.forEach((bar) => {
      if (bar instanceof HTMLElement && !previousQualified.has(bar)) {
        previousQualified.set(bar, isQualified(bar));
      }
    });

    reconcileBars();
  });
});
